// Reduced-motion composable for the redesigned marketing site.
//
// Exposes a reactive boolean that tracks the user's `prefers-reduced-motion`
// system setting. Motion components (e.g. ScrollReveal, transitions) consult
// this to disable non-essential animation when reduced motion is requested,
// while loading indicators and form-validation feedback remain unaffected
// (Requirements 8.7, 9.7).
//
// The composable is SSR/test-safe: it guards every access to `window` and
// `matchMedia`, so it degrades to a stable `false` (motion allowed) in
// environments where those APIs are unavailable, and never throws.

import { ref, onScopeDispose } from 'vue'

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

// Detect whether matchMedia is available in the current environment.
function canMatchMedia () {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function'
  )
}

/**
 * Track the user's reduced-motion preference reactively.
 *
 * @returns {{ prefersReducedMotion: import('vue').Ref<boolean> }}
 *   `prefersReducedMotion` is `true` when the system requests reduced motion,
 *   and `false` otherwise (including when the API is unavailable).
 */
export function useReducedMotion () {
  const prefersReducedMotion = ref(false)

  if (!canMatchMedia()) {
    // SSR or a test/JSDOM environment without matchMedia: assume motion is
    // allowed and skip listener wiring. Returning a stable ref keeps callers
    // simple and side-effect free.
    return { prefersReducedMotion }
  }

  const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY)
  prefersReducedMotion.value = mediaQuery.matches

  const update = (event) => {
    prefersReducedMotion.value = event.matches
  }

  // addEventListener is the modern API; fall back to the deprecated
  // addListener for older browsers that still ship it.
  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', update)
  } else if (typeof mediaQuery.addListener === 'function') {
    mediaQuery.addListener(update)
  }

  // Clean up the listener when the owning effect scope (component) is disposed
  // to avoid leaks. onScopeDispose is a no-op outside a setup/effect scope.
  onScopeDispose(() => {
    if (typeof mediaQuery.removeEventListener === 'function') {
      mediaQuery.removeEventListener('change', update)
    } else if (typeof mediaQuery.removeListener === 'function') {
      mediaQuery.removeListener(update)
    }
  })

  return { prefersReducedMotion }
}

export default useReducedMotion
